import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Star, CheckCircle, ArrowRight } from 'lucide-react';
import './Home.css';

const Home = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="display-large fade-in-up">
              Lawn Coring & Verti-Mowing in Perth's Northern Suburbs
            </h1>
            <p className="body-large fade-in-up" style={{ animationDelay: '0.2s' }}>
              Remove thatch, eliminate 'spongy' lawn, and kickstart healthy regrowth — fast, professional service.
            </p>
            <div className="hero-buttons fade-in-up" style={{ animationDelay: '0.4s' }}>
              <a href="tel:0417910217" className="btn-cta">
                <Phone size={20} />
                Call 0417 910 217
              </a>
            </div>
            <div className="social-proof fade-in-up" style={{ animationDelay: '0.6s' }}>
              <div className="proof-item">
                <Star size={20} fill="#D3FF62" color="#D3FF62" />
                <Star size={20} fill="#D3FF62" color="#D3FF62" />
                <Star size={20} fill="#D3FF62" color="#D3FF62" />
                <Star size={20} fill="#D3FF62" color="#D3FF62" />
                <Star size={20} fill="#D3FF62" color="#D3FF62" />
                <span className="body-medium">5.0 Rating</span>
              </div>
              <div className="proof-divider">|</div>
              <div className="proof-item">
                <span className="body-medium">14 Google Reviews</span>
              </div>
              <div className="proof-divider">|</div>
              <div className="proof-item">
                <span className="body-medium">Ballajura-based</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="network-grid">
            <div className="network-card">
              <CheckCircle size={40} color="#004534" />
              <h3 className="heading-3" style={{ marginTop: '16px' }}>Remove Thatch & Sponge</h3>
              <p className="body-medium">
                Eliminate built-up dead grass layer that makes your lawn feel soft and spongy. Get back to a firm, healthy base.
              </p>
            </div>
            <div className="network-card">
              <CheckCircle size={40} color="#004534" />
              <h3 className="heading-3" style={{ marginTop: '16px' }}>Healthier Regrowth in Weeks</h3>
              <p className="body-medium">
                Verti-mowing cuts right back to the root, removing old leaf and encouraging fresh, vigorous new growth.
              </p>
            </div>
            <div className="network-card">
              <CheckCircle size={40} color="#004534" />
              <h3 className="heading-3" style={{ marginTop: '16px' }}>Fast Response & Reliable Service</h3>
              <p className="body-medium">
                Same-day or next-day service available. Over 20 years of experience keeping Perth lawns in top condition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
            How It Works
          </h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3 className="heading-3">Call or Message</h3>
              <p className="body-medium">
                Get in touch for a quick quote. We'll ask about your lawn size, condition, and any specific concerns.
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3 className="heading-3">We Assess & Advise</h3>
              <p className="body-medium">
                We'll evaluate your lawn's thatch buildup and recommend the best approach — verti-mowing, coring, or both.
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3 className="heading-3">Verti-Mow or Core</h3>
              <p className="body-medium">
                We remove the dead layer, relieve compaction, and prepare your lawn for healthy regrowth.
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">4</div>
              <h3 className="heading-3">Aftercare Guidance</h3>
              <p className="body-medium">
                We'll provide simple tips on watering, mowing height, and optional fertiliser to maximize results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
            Professional Lawn Care Service
          </h2>
          <div className="gallery-grid">
            <div className="gallery-item">
              <img
                src="https://images.unsplash.com/photo-1721138941961-cb245dd928af?w=600&q=80"
                alt="Professional lawn maintenance equipment"
                className="gallery-image"
              />
              <p className="caption" style={{ marginTop: '8px', textAlign: 'center' }}>Professional Equipment</p>
            </div>
            <div className="gallery-item">
              <img
                src="https://images.pexels.com/photos/20727438/pexels-photo-20727438.jpeg?w=600&q=80"
                alt="Professional lawn care service"
                className="gallery-image"
              />
              <p className="caption" style={{ marginTop: '8px', textAlign: 'center' }}>Expert Lawn Treatment</p>
            </div>
            <div className="gallery-item">
              <img
                src="https://images.unsplash.com/photo-1734303023491-db8037a21f09?w=600&q=80"
                alt="Quality lawn maintenance"
                className="gallery-image"
              />
              <p className="caption" style={{ marginTop: '8px', textAlign: 'center' }}>Quality Results</p>
            </div>
          </div>
          <p className="body-small" style={{ textAlign: 'center', marginTop: 'var(--spacing-medium)', color: 'var(--text-light)' }}>
            See our actual work - check our 5.0★ Google reviews with customer photos, or call for recent before/after examples
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
            What Our Customers Say
          </h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="stars">
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
              </div>
              <p className="body-medium">
                "Highly recommended for verti-mowing. Removed nearly 10 years of unwanted thatch. My lawn was spongy — now it's the best lawn in the street again."
              </p>
            </div>
            <div className="testimonial-card">
              <div className="stars">
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
              </div>
              <p className="body-medium">
                "Peter did an excellent job. Takes the lawn right back to the root and regrows new leaf. Sponge removed and now firm."
              </p>
            </div>
            <div className="testimonial-card">
              <div className="stars">
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
              </div>
              <p className="body-medium">
                "Fantastic job. Called and he was at my house that day. Made the garden look great again."
              </p>
            </div>
            <div className="testimonial-card">
              <div className="stars">
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
              </div>
              <p className="body-medium">
                "I've used Peter for over 20 years. Professional, courteous, reliable, with great knowledge. Reasonably priced."
              </p>
            </div>
            <div className="testimonial-card">
              <div className="stars">
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
              </div>
              <p className="body-medium">
                "Fast service, knowledgeable. My lawn needed serious work most wouldn't touch. Highly recommend."
              </p>
            </div>
            <div className="testimonial-card">
              <div className="stars">
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
              </div>
              <p className="body-medium">
                "Reliable, reasonably priced, with great advice on keeping the lawn maintained."
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 'var(--spacing-large)' }}>
            <Link to="/reviews" className="btn-secondary">
              Read All Reviews
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
            Frequently Asked Questions
          </h2>
          <div className="faq-list">
            <div className="faq-item">
              <h3 className="heading-3">What is verti-mowing?</h3>
              <p className="body-medium">
                Verti-mowing (also called vertical mowing or dethatching) uses vertical blades to cut through and remove the built-up layer of dead grass (thatch) that sits on top of the soil. It helps your lawn breathe and regrow healthier.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="heading-3">What is lawn coring or aeration?</h3>
              <p className="body-medium">
                Lawn coring removes small plugs of soil to relieve compaction and improve air, water, and nutrient penetration to the roots. It's especially helpful for hard, compacted lawns.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="heading-3">Will my lawn look worse before it looks better?</h3>
              <p className="body-medium">
                Yes, typically. Verti-mowing cuts right back to the root level, so your lawn will look very short and sparse immediately after. However, within a few weeks of proper watering and care, healthy new growth comes through.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="heading-3">How long until my lawn regrows?</h3>
              <p className="body-medium">
                Most lawns show noticeable regrowth within 2-4 weeks. Full recovery and a thicker, healthier lawn usually takes 6-8 weeks, depending on the season, watering, and lawn type.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="heading-3">When is the best time of year in Perth?</h3>
              <p className="body-medium">
                Spring (September-November) and early autumn (March-April) are ideal. The lawn is actively growing and can recover quickly. Avoid mid-summer or mid-winter when growth is slow.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="heading-3">Do you service my suburb?</h3>
              <p className="body-medium">
                We primarily service Perth's Northern Suburbs (Ballajura, Malaga, Morley, Dianella, Balcatta, and surrounding areas). Contact us to confirm if we cover your specific suburb.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="container">
          <div className="cta-banner">
            <h2 className="heading-1" style={{ color: 'white' }}>
              Ready to Bring Your Lawn Back?
            </h2>
            <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.9)', marginTop: '16px' }}>
              Fast response, professional service, and a healthier lawn in weeks.
            </p>
            <div className="cta-buttons">
              <a href="tel:0417910217" className="btn-cta" style={{ background: 'white', color: 'var(--brand-dark)' }}>
                <Phone size={20} />
                Call 0417 910 217
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Sticky CTA */}
      <div className="mobile-cta-sticky">
        <a href="tel:0417910217" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
          <Phone size={18} />
          Call Now: 0417 910 217
        </a>
      </div>
    </div>
  );
};

export default Home;